import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MARCADORES_POR_FOLHA, type PoseDoMarcador } from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import { Pagina } from "../../components/Pagina.js";
import {
  Area,
  Botao,
  Campo,
  Entrada,
  Falha,
  Girando,
  Selecao,
  Vazio,
  cx,
} from "../../components/ui.js";
import { BlocoDaFoto } from "./BlocoDaFoto.js";
import { CapasDoLivro } from "./CapasDoLivro.js";
import { ConjuntosFeitos } from "./ConjuntosFeitos.js";
import { EstilosDeArte } from "./EstilosDeArte.js";
import { FaixaDeProgresso, useEventoDoProgresso } from "./FaixaDeProgresso.js";
import { FolhaDeMarcadores } from "./FolhaDeMarcadores.js";
import { arteInicial, pedidoDeArte } from "./estilos.js";
import { FilaDeImpressao } from "../impressao/FilaDeImpressao.js";
import { fonteParaColocar } from "../impressao/folhas.js";
import { useFotosPublicadas } from "../impressao/useFotosPublicadas.js";

/**
 * Marcadores de livro: três artes por folha da SELPHY, criadas pela IA a partir de
 * uma foto publicada (e, se houver, da capa do livro).
 *
 * Cada marcador é uma chamada paga ao modelo de imagem, por isso o consumo é por
 * marcador entregue e a tela confirma antes de começar. O resultado fica guardado
 * em "Já feitos": se a pessoa sair da tela no meio, o conjunto aparece lá.
 */
export function MarcadoresPage() {
  const [parametros] = useSearchParams();
  const fotoDaUrl = Number(parametros.get("foto"));

  const fotos = useFotosPublicadas(
    Number.isInteger(fotoDaUrl) && fotoDaUrl > 0 ? [fotoDaUrl] : undefined,
  );
  const capas = trpc.bookCovers.list.useQuery();
  const modelos = trpc.modelosDeMarcador.list.useQuery();
  const feitos = trpc.marcadores.list.useQuery();
  const saldo = trpc.lotes.saldo.useQuery();
  const utils = trpc.useUtils();

  const [fotoId, setFotoId] = useState<number | null>(
    Number.isInteger(fotoDaUrl) && fotoDaUrl > 0 ? fotoDaUrl : null,
  );
  const [capaId, setCapaId] = useState<number | null>(null);
  const [titulo, setTitulo] = useState("");
  const [subtitulo, setSubtitulo] = useState("");
  const [frase, setFrase] = useState("");
  const [rodape, setRodape] = useState("");
  const [observacao, setObservacao] = useState("");
  const [pose, setPose] = useState<PoseDoMarcador>("manter");
  // Como a arte sai (automático, três estilos, um modelo ou texto à mão) e o que se preencheu em cada.
  const [arte, setArte] = useState(arteInicial);
  const [confirmando, setConfirmando] = useState(false);
  const [aberto, setAberto] = useState<number | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const resultado = useRef<HTMLElement>(null);
  const formulario = useRef<HTMLElement>(null);

  /** Escolher um modelo sugere os campos que o operador ainda não preencheu. */
  function escolherModelo(valor: string) {
    setArte((a) => ({ ...a, modeloId: valor, ajustadas: null }));
    const m = (modelos.data ?? []).find((x) => String(x.id) === valor);
    if (!m) return;
    const padrao = m.camposPadrao;
    if (!titulo && padrao.titulo) setTitulo(padrao.titulo);
    if (!subtitulo && padrao.subtitulo) setSubtitulo(padrao.subtitulo);
    if (!frase && padrao.frase) setFrase(padrao.frase);
    if (!rodape && padrao.rodape) setRodape(padrao.rodape);
    if (!observacao && m.observacao) setObservacao(m.observacao);
  }

  const resultadoDaArte = pedidoDeArte(arte, modelos.data ?? []);

  const gerar = trpc.marcadores.gerar.useMutation({
    onSuccess: async (r) => {
      setAberto(r.id);
      setAviso(
        r.cobradas < MARCADORES_POR_FOLHA
          ? `A IA entregou ${r.cobradas} de ${MARCADORES_POR_FOLHA}: refaça o que faltou, 1 foto cada.`
          : null,
      );
      await Promise.all([utils.marcadores.list.invalidate(), utils.lotes.saldo.invalidate()]);
    },
  });

  // O que a faixa de progresso mostra: criar a folha (daqui) ou refazer uma faixa (do FolhaDeMarcadores).
  const evento = useEventoDoProgresso({
    estado: gerar.status,
    enviadoEm: gerar.submittedAt,
    ...(gerar.data && { entregues: gerar.data.cobradas, faltam: gerar.data.faltam }),
    ...(gerar.error && { mensagem: gerar.error.message }),
  });

  /** Rola até o alvo sem deixar a barra do app e a faixa cobrirem o começo, e sem animar a quem pediu menos movimento. */
  function irPara(alvo: HTMLElement | null) {
    const reduzido = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    alvo?.scrollIntoView({ behavior: reduzido ? "auto" : "smooth", block: "start" });
  }

  // Ao terminar de gerar, leva a pessoa ao resultado: no celular ele fica abaixo do formulário.
  useEffect(() => {
    if (gerar.isSuccess) irPara(resultado.current);
  }, [gerar.isSuccess]);

  const restantes = saldo.data?.restantes;
  const faltamFotos = typeof restantes === "number" && restantes < MARCADORES_POR_FOLHA;
  const pronto = fotoId !== null && titulo.trim() !== "" && resultadoDaArte.pronta;

  function comecar() {
    if (fotoId === null || !resultadoDaArte.pronta) return;
    setConfirmando(false);
    setAviso(null);
    gerar.mutate({
      geracaoId: fotoId,
      // A versão vem da chave, e só vale para a foto que a tem; sem versão separada,
      // a peça já é a foto.
      // Só a foto da IA vai como versão: a original não serve de referência ao marcador (#94).
      ...(fonteParaColocar(fotos.vistas.get(fotoId), fotos.versao) === "foto" && {
        fonte: "foto" as const,
      }),
      ...(capaId !== null && { capaId }),
      // Só o que o modo escolhido pede: Automático não manda nada, e o servidor escolhe.
      ...resultadoDaArte.pedido,
      ...(pose === "ajustar" && { pose }),
      campos: { titulo, subtitulo, frase, rodape },
      observacao,
    });
  }

  /** "Tentar de novo" da faixa. Se o formulário já não está pronto (a pessoa o mexeu depois do erro), leva até ele. */
  function repetir() {
    if (pronto && !faltamFotos) comecar();
    else irPara(formulario.current);
  }

  return (
    <Pagina
      titulo="Marcadores de livro"
      descricao="A IA cria a arte de três marcadores a partir da foto publicada, prontos para imprimir numa folha da SELPHY e cortar."
    >
      <FaixaDeProgresso
        evento={evento}
        aoVer={() => irPara(resultado.current)}
        aoRepetir={repetir}
      />

      <div className="grid gap-8 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        {/* Gerando, o formulário não recebe toque nem foco e esmaece — menos o pé, onde o botão diz "Criando…".
            Mexer nos campos no meio da geração não mudaria o que já foi pedido, só confundiria. */}
        <section
          ref={formulario}
          aria-label="Montar os marcadores"
          aria-busy={gerar.isPending}
          inert={gerar.isPending}
          className={cx(
            "rolar-sob-a-faixa flex min-w-0 flex-col gap-6 [&>:not(:last-child)]:transition-opacity [&>:not(:last-child)]:duration-200",
            gerar.isPending && "[&>:not(:last-child)]:opacity-55",
          )}
        >
          <BlocoDaFoto bandeja={fotos} fotoId={fotoId} aoEscolher={setFotoId} />

          <CapasDoLivro capas={capas.data ?? []} escolhida={capaId} aoEscolher={setCapaId} />

          <div className="flex flex-col gap-4">
            <h2 className="text-[0.9375rem] font-medium text-texto">Textos</h2>
            <Campo rotulo="Título">
              <Entrada
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                maxLength={50}
                placeholder="Mateus 24"
              />
            </Campo>
            <Campo rotulo="Subtítulo (opcional)">
              <Entrada
                value={subtitulo}
                onChange={(e) => setSubtitulo(e.target.value)}
                maxLength={60}
                placeholder="Ainda não é o fim"
              />
            </Campo>
            <Campo rotulo="Frase (opcional)">
              <Entrada
                value={frase}
                onChange={(e) => setFrase(e.target.value)}
                maxLength={80}
                placeholder="Quem lê, atenda."
              />
            </Campo>
            <Campo
              rotulo="Rodapé (opcional)"
              dica="Até duas linhas, em geral os sites. Só este texto aparece escrito na arte."
            >
              <Area
                value={rodape}
                onChange={(e) => setRodape(e.target.value)}
                maxLength={100}
                rows={2}
                className="min-h-[4.5rem]"
                placeholder={"portalser.org\nespiritismo.tv"}
              />
            </Campo>
          </div>

          <div className="flex flex-col gap-4">
            <h2 className="text-[0.9375rem] font-medium text-texto">Arte</h2>
            <EstilosDeArte
              arte={arte}
              aoMudar={setArte}
              modelos={modelos.data ?? []}
              aoEscolherModelo={escolherModelo}
              falta={
                resultadoDaArte.pronta || resultadoDaArte.intocada ? null : resultadoDaArte.falta
              }
            />

            <Campo
              rotulo="Pose das pessoas"
              dica={
                pose === "ajustar"
                  ? "A IA pode virar as pessoas de frente e fazê-las segurar o livro, mantendo rosto, cabelo, idade e roupa. Mudar a pose aumenta o risco de a IA se afastar da foto: confira os rostos antes de imprimir."
                  : "As pessoas continuam fazendo o que a foto mostra; o livro escolhido entra no lugar do que a foto tiver."
              }
            >
              <Selecao value={pose} onChange={(e) => setPose(e.target.value as PoseDoMarcador)}>
                <option value="manter">Manter a pose da foto</option>
                <option value="ajustar">Ajustar ao marcador</option>
              </Selecao>
            </Campo>

            <Campo rotulo="Observação para a IA (opcional)" dica="Por exemplo: tons de Páscoa.">
              <Entrada
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                maxLength={300}
              />
            </Campo>
          </div>

          <div className="flex flex-col gap-3 border-t border-borda pt-4">
            {faltamFotos && (
              <p className="text-[0.8125rem] text-texto-suave">
                O pacote tem {restantes} {restantes === 1 ? "foto" : "fotos"}, e um conjunto usa{" "}
                {MARCADORES_POR_FOLHA}.
              </p>
            )}

            {confirmando && !gerar.isPending && (
              <div className="rounded-peca border border-borda bg-superficie p-3">
                <p className="text-[0.875rem] text-texto">
                  Consome {MARCADORES_POR_FOLHA} fotos do pacote
                  {typeof restantes === "number" ? ` (restam ${restantes})` : ""}, uma por marcador
                  que a IA entregar. Continuar?
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Botao tom="brasa" onClick={comecar}>
                    Continuar
                  </Botao>
                  <Botao tom="quieto" onClick={() => setConfirmando(false)}>
                    Cancelar
                  </Botao>
                </div>
              </div>
            )}

            {!confirmando && (
              // O andamento está na faixa do topo: o botão só diz o estado, sem um segundo spinner.
              <Botao
                tom="brasa"
                largo
                disabled={!pronto || faltamFotos || gerar.isPending}
                onClick={() => setConfirmando(true)}
              >
                {gerar.isPending ? "Criando…" : `Gerar ${MARCADORES_POR_FOLHA} marcadores`}
              </Botao>
            )}

            {gerar.isError && <Falha mensagem={gerar.error.message} />}
          </div>
        </section>

        <section
          ref={resultado}
          aria-label="Resultado"
          className="rolar-sob-a-faixa flex min-w-0 flex-col gap-8"
        >
          <div>
            <h2 className="mb-3 text-[0.9375rem] font-medium text-texto">Folha de marcadores</h2>
            {aberto !== null ? (
              <>
                {aviso && <p className="mb-3 text-[0.8125rem] text-texto-suave">{aviso}</p>}
                <FolhaDeMarcadores
                  key={aberto}
                  id={aberto}
                  restantes={restantes}
                  aoApagar={() => setAberto(null)}
                />
              </>
            ) : (
              <Vazio
                titulo="Nenhum conjunto aberto"
                descricao="Escolha a foto, preencha os textos e gere. Os conjuntos já feitos ficam abaixo."
              />
            )}
          </div>

          {/* A mesma fila da tela de Impressão: o que se manda daqui entra nela. Só aparece havendo trabalho. */}
          <FilaDeImpressao />

          <div>
            <h2 className="mb-3 text-[0.9375rem] font-medium text-texto">Já feitos</h2>
            {feitos.isLoading ? (
              <Girando className="mx-auto my-6 size-5 text-texto-fraco" />
            ) : feitos.isError ? (
              <Falha
                mensagem={feitos.error.message}
                tentarNovamente={() => void feitos.refetch()}
              />
            ) : (feitos.data ?? []).length === 0 ? (
              <p className="text-[0.875rem] text-texto-suave">Nenhum conjunto feito ainda.</p>
            ) : (
              <ConjuntosFeitos
                feitos={feitos.data!}
                aberto={aberto}
                aoAbrir={(id) => {
                  setAviso(null);
                  setAberto(id);
                }}
              />
            )}
          </div>
        </section>
      </div>
    </Pagina>
  );
}
